import { useMemo, useRef, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import styled from "styled-components";

import { Button } from "../components/Button.tsx";
import { Stack } from "../components/Stack.tsx";
import { SpeakerCard } from "../components/SpeakerCard.tsx";
import { SaveIndicator } from "../components/SaveIndicator.tsx";
import { useSamplePlayer } from "../hooks/useSamplePlayer.ts";
import { useAudioSrcRefresh } from "../hooks/useAudioSrcRefresh.ts";
import { useAutoSave } from "../hooks/useAutoSave.ts";
import { useNavigateTo, useSyncParams, useRequireMeetingData } from "../routing.ts";

import {
  profilesAtom,
  customSpeakersAtom,
  thresholdAtom,
  audioFilenameAtom,
  audioSrcAtom,
  currentProcessingIdAtom,
} from "../../state/atoms.ts";
import {
  audioUrlAtom,
  detectedSpeakersAtom,
  speakerListAtom,
  samplesPerSpeakerAtom,
  tokenSegmentsAtom,
  canProceedFromIdentifyAtom,
} from "../../state/derived.ts";
import type { SpeakerProfile } from "../../lib/types.ts";

export function IdentifyScreen() {
  // Renueva la PAR de audio.wav / audio.mp4 automáticamente si el audio viene del bucket.
  useAudioSrcRefresh();

  // Fase 14 — autoguardado debounced + on-blur.
  const { resolveConflict } = useAutoSave();

  const processingId = useAtomValue(currentProcessingIdAtom);
  const audioUrl = useAtomValue(audioUrlAtom);
  const detected = useAtomValue(detectedSpeakersAtom);
  const allSpeakers = useAtomValue(speakerListAtom);
  const samplesPerSpeaker = useAtomValue(samplesPerSpeakerAtom);
  const tokenSegments = useAtomValue(tokenSegmentsAtom);
  const threshold = useAtomValue(thresholdAtom);
  const canProceed = useAtomValue(canProceedFromIdentifyAtom);
  const audioFilename = useAtomValue(audioFilenameAtom);
  const audioSrc = useAtomValue(audioSrcAtom);

  const [profiles, setProfiles] = useAtom(profilesAtom);
  const [customSpeakers, setCustomSpeakers] = useAtom(customSpeakersAtom);
  const navigateTo = useNavigateTo();
  useSyncParams();
  useRequireMeetingData();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [{ playingId, progress }, { play }] = useSamplePlayer(audioRef);

  // Cuando el procesamiento es MP4, reutilizamos la misma PAR del audio
  // como fuente de video. useAudioSrcRefresh la mantiene vigente.
  const isMp4 = audioFilename === "audio.mp4";
  const videoUrl = isMp4 ? (audioSrc?.url ?? null) : null;

  // Contador para forzar el remount del <video> cuando el usuario re-clickea
  // el mismo sample. Sin esto, hacer click sobre el sample que ya está sonando
  // no reinicia el video (el audio sí, porque useSamplePlayer reseekea).
  const [playKey, setPlayKey] = useState(0);

  // Conteo de segmentos por speaker para mostrar en SpeakerCard.
  const segsBySpeaker = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of tokenSegments) {
      map.set(s.speakerIndex, (map.get(s.speakerIndex) ?? 0) + 1);
    }
    return map;
  }, [tokenSegments]);

  const handlePlaySample = (
    id: string,
    startTime: number,
    duration: number,
  ) => {
    setPlayKey((k) => k + 1);
    void play(id, startTime, {
      duration,
      paddingBefore: 2,
      paddingAfter: 2,
    });
  };

  const handleChangeProfile = (
    speakerIndex: number,
    patch: Partial<SpeakerProfile>,
  ) => {
    const next = new Map(profiles);
    const existing = next.get(speakerIndex);
    next.set(speakerIndex, {
      index: speakerIndex,
      name: existing?.name ?? "",
      role: existing?.role ?? "",
      ...patch,
    });
    setProfiles(next);
  };

  const handleAddManualSpeaker = () => {
    const min = customSpeakers.length === 0 ? 0 : Math.min(...customSpeakers);
    const newIndex = min - 1;
    setCustomSpeakers([...customSpeakers, newIndex]);
  };

  const handleRemoveManualSpeaker = (index: number) => {
    setCustomSpeakers(customSpeakers.filter((sp) => sp !== index));
    const nextProfiles = new Map(profiles);
    nextProfiles.delete(index);
    setProfiles(nextProfiles);
  };

  const goBack = () => navigateTo.meetings();
  const goNext = () => {
    if (!processingId) return;
    const hasLowConf = tokenSegments.some((s) => s.avgConfidence < threshold);
    if (hasLowConf) navigateTo.review(processingId);
    else navigateTo.export(processingId);
  };

  return (
    <Container>
      <Header>
        <Title>Identificar speakers</Title>
        <Subtitle>
          Escucha los fragmentos y asigna nombre y cargo a cada voz.
        </Subtitle>
      </Header>

      {/*
        En MP4 el audio se silencia: usamos el <audio> sólo como fuente de
        timing para useSamplePlayer (clearTimer, progress, auto-stop). El
        sonido lo aporta el <video> inline que monta SpeakerCard cuando un
        chunk está sonando. Sin mute habría doble audio (audio + video).

        onError (Fase 25): se dispara si el browser no puede cargar el src
        — PAR vencida, 404 al consumir, red caída. Sin esto el sample
        correría con los timers pero sin sonido y no tendríamos trace.
      */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          muted={isMp4}
          onError={(e) => {
            const mediaError = e.currentTarget.error;
            console.warn("[IdentifyScreen] <audio> error event", {
              src: audioUrl,
              code: mediaError?.code,
              message: mediaError?.message,
            });
          }}
        />
      )}

      <Stack $gap="md">
        {detected.map((sp) => {
          const segCount = segsBySpeaker.get(sp) ?? 0;
          const segTotal = tokenSegments.length;
          const samples = samplesPerSpeaker.get(sp) ?? [];
          const profile = profiles.get(sp);
          return (
            <SpeakerCard
              key={sp}
              speakerIndex={sp}
              segmentCount={segCount}
              segmentTotal={segTotal}
              samples={samples}
              profile={profile}
              playingId={playingId}
              progress={progress}
              videoUrl={videoUrl}
              playKey={playKey}
              onPlaySample={handlePlaySample}
              onChangeProfile={(patch: Partial<SpeakerProfile>) => handleChangeProfile(sp, patch)}
            />
          );
        })}

        {customSpeakers.map((sp) => {
          const profile = profiles.get(sp);
          return (
            <SpeakerCard
              key={sp}
              speakerIndex={sp}
              isManual
              segmentCount={0}
              segmentTotal={tokenSegments.length}
              samples={[]}
              profile={profile}
              playingId={playingId}
              progress={progress}
              onPlaySample={() => {}}
              onChangeProfile={(patch: Partial<SpeakerProfile>) => handleChangeProfile(sp, patch)}
              onRemove={() => handleRemoveManualSpeaker(sp)}
            />
          );
        })}

        <AddSpeakerWrapper>
          <Button $variant="secondary" onClick={handleAddManualSpeaker}>
            + Agregar speaker manualmente
          </Button>
          <AddHint>
            Útil si Oracle no detectó a alguien que sí participó.
          </AddHint>
        </AddSpeakerWrapper>
      </Stack>

      {/* Fase 14 — banner de conflicto o badge de estado de guardado */}
      <SaveIndicator onResolveConflict={resolveConflict} />

      <Footer>
        <Button $variant="ghost" onClick={goBack}>
          ← Volver
        </Button>
        <FooterRight>
          <SpeakerCount>
            {allSpeakers.length} {allSpeakers.length === 1 ? "speaker" : "speakers"} en total
          </SpeakerCount>
          <Button onClick={goNext} disabled={!canProceed}>
            Continuar →
          </Button>
        </FooterRight>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xl};
`;
const Header = styled.div``;
const Title = styled.h1`
  font-size: ${({ theme }) => theme.font.size.xxl};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  margin: 0 0 ${({ theme }) => theme.space.xs} 0;
  color: ${({ theme }) => theme.color.textPrimary};
  letter-spacing: -0.02em;
`;
const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
`;
const AddSpeakerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  align-items: flex-start;
  padding: ${({ theme }) => theme.space.sm} 0;
`;
const AddHint = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.color.border};
`;
const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;
const SpeakerCount = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.mono};
`;
